/**
 * Public verdict page — treuthat.com/v/:shortId
 *
 * Renders a shared verification for anyone holding the link, no account
 * required. Anonymous by design: the payload never identifies the verifier.
 */

import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { VerdictCard, type VerdictCardProps } from '../components/VerdictCard'

// Canonical US-storefront listing URL — the app is distributed on the US
// storefront only, so the storefront segment must be pinned; the bare
// /app/id… form resolves against the visitor's storefront and dead-ends
// for anyone outside the US.
const APP_STORE_URL = 'https://apps.apple.com/us/app/treu/id6769203130'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.treuapp.mobile'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'

type PageState =
  | { status: 'loading' }
  /** 404/410 — the link is genuinely dead (revoked or never existed). */
  | { status: 'gone' }
  /** 429 / 5xx / network — the backend hiccuped; the link is NOT dead. */
  | { status: 'transient' }
  | { status: 'loaded'; verdict: VerdictCardProps }

export function PublicVerdictPage() {
  const { shortId } = useParams<{ shortId: string }>()
  const [state, setState] = useState<PageState>({ status: 'loading' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!shortId) {
      setState({ status: 'gone' })
      return
    }
    let cancelled = false
    setState({ status: 'loading' })

    // Deliberately a bare fetch, NOT the shared axios client: this resource
    // is public, and the client's withCredentials + 401 interceptor could
    // log an authenticated viewer out over an unrelated auth hiccup.
    fetch(`${API_BASE_URL}/shares/${encodeURIComponent(shortId)}`)
      .then(async (res) => {
        if (!res.ok) {
          // Only 404/410 mean the link itself is dead. Everything else —
          // 429 (a popular link tripping the share rate limit), 5xx, an
          // outage — is transient and must NOT tell visitors the link was
          // revoked.
          if (!cancelled) {
            setState(res.status === 404 || res.status === 410 ? { status: 'gone' } : { status: 'transient' })
          }
          return
        }
        const body = await res.json()
        if (!cancelled) {
          setState({
            status: 'loaded',
            verdict: {
              claim: body.data.claim,
              verdict: body.data.verdict,
              confidence: body.data.confidence,
              headline: body.data.headline,
              shortAnswer: body.data.shortAnswer,
              confidenceBasis: body.data.confidenceBasis,
              sources: body.data.sources ?? [],
              verifiedAt: body.data.verifiedAt,
            },
          })
        }
      })
      .catch(() => {
        // Network failure / malformed body — retryable, not "revoked".
        if (!cancelled) setState({ status: 'transient' })
      })

    return () => {
      cancelled = true
    }
  }, [shortId, attempt])

  useEffect(() => {
    document.title = 'Treu — Verified claim'
  }, [])

  return (
    <div className="min-h-screen bg-paper-0 flex flex-col">
      <header className="bg-brand-950 px-6 py-4">
        <Link to="/" className="inline-block">
          <img src="/assets/logo-white.png" alt="Treu" className="h-7 w-auto" />
        </Link>
      </header>

      <main className="flex-1 w-full max-w-2xl mx-auto px-4 py-10">
        {state.status === 'loading' && (
          <div className="text-center text-ink-400 py-20">Loading verdict…</div>
        )}

        {state.status === 'gone' && (
          <div className="text-center py-20">
            <div className="font-display font-semibold text-ink-900 text-lg mb-2">
              This shared verdict is no longer available
            </div>
            <div className="text-sm text-ink-600">
              The link may have been revoked, or it never existed.
            </div>
          </div>
        )}

        {state.status === 'transient' && (
          <div className="text-center py-20">
            <div className="font-display font-semibold text-ink-900 text-lg mb-2">
              Taking a moment
            </div>
            <div className="text-sm text-ink-600 mb-4">
              This verdict couldn&rsquo;t be loaded right now — please try again shortly.
            </div>
            <button
              type="button"
              onClick={() => setAttempt((a) => a + 1)}
              className="px-4 py-2 rounded-lg bg-brand-950 text-white text-sm font-medium hover:bg-brand-800 transition-colors"
            >
              Try again
            </button>
          </div>
        )}

        {state.status === 'loaded' && <VerdictCard {...state.verdict} />}

        <section className="mt-10 text-center">
          <div className="font-display font-semibold text-ink-900 text-lg mb-1">
            Check claims before you share them
          </div>
          <div className="text-sm text-ink-600 mb-4">
            Treu verifies claims against real sources in seconds.
          </div>
          <div className="flex items-center justify-center gap-3">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg bg-brand-950 text-white text-sm font-medium no-underline hover:bg-brand-800 transition-colors"
            >
              Download on the App Store
            </a>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg bg-brand-950 text-white text-sm font-medium no-underline hover:bg-brand-800 transition-colors"
            >
              Get it on Google Play
            </a>
          </div>
          <div className="mt-3 text-sm">
            <Link to="/" className="text-accent-600 no-underline hover:underline">
              or verify on the web
            </Link>
          </div>
        </section>
      </main>
    </div>
  )
}
